<!--
  MetricPicker.svelte — a searchable, category-grouped metric picker.

  Shared by the Metrics page (multi-select) and the dashboard widget editor
  (single-select), so the catalogue reads the same wherever it is browsed.

  Categories start collapsed: the registry holds seventy-odd metrics, and an
  expanded list of all of them is a wall rather than a menu.
-->
<script lang="ts">
	import { Badge, Checkbox, Helper, Input } from 'flowbite-svelte';
	import { t } from '../../../i18n';
	import type { AnalyticsCatalog, AnalyticsMetric } from '../../../AppTypes';

	interface Props {
		/** The metric catalogue, or undefined while it loads. */
		catalog?: AnalyticsCatalog;
		/** Multi-select (checkboxes) rather than single-select (click to choose). */
		multiple?: boolean;
		/** Multi-select model, keyed by metric key. Bind from the parent. */
		selected?: Record<string, boolean>;
		/** Single-select model: the chosen metric key. Bind from the parent. */
		value?: string;
		/** Placeholder for the search box. */
		placeholder?: string;
		/** Whether the list scrolls within a fixed height, for use inside a modal. */
		scrollable?: boolean;
	}

	let {
		catalog = undefined,
		multiple = false,
		selected = $bindable({}),
		value = $bindable(''),
		placeholder = 'Search metrics…',
		scrollable = false
	}: Props = $props();

	/** What has been typed into the search box. */
	let search = $state('');
	/** Which categories the reader has collapsed, keyed by category name. */
	let collapsed: Record<string, boolean> = $state({});
	/** Whether the initial collapsed state has been applied. */
	let initialized = $state(false);

	/** One category's worth of the picker. */
	interface Section {
		/** The category name. */
		name: string;
		/** The metrics in it that match the current search. */
		metrics: AnalyticsMetric[];
		/** Whether its metrics are visible. */
		expanded: boolean;
		/** How many of its metrics are currently chosen. */
		selectedCount: number;
	}

	/*
	 * Start with every category collapsed once the catalogue arrives. In single-select,
	 * expand the category holding the current choice, so an existing selection is visible
	 * rather than hidden behind a heading.
	 */
	$effect(() => {
		if (catalog == null || initialized) return;
		initialized = true;
		collapsed = Object.fromEntries(catalog.Categories.map((category) => [category, true]));
		if (!multiple && value.length > 0) {
			const category = catalog.Metrics.find((metric) => metric.Key === value)?.Category;
			if (category != null) collapsed = { ...collapsed, [category]: false };
		}
	});

	/** The categories to render, filtered by the search and in catalogue order. */
	let sections: Section[] = $derived.by(() => {
		if (catalog == null) return [];
		const query = search.trim().toLowerCase();
		/** Whether a metric matches what has been typed. */
		const fnMatches = (metric: AnalyticsMetric): boolean =>
			query.length === 0 ||
			metric.Label.toLowerCase().includes(query) ||
			metric.Key.toLowerCase().includes(query) ||
			metric.Category.toLowerCase().includes(query);
		/** Whether a metric is currently chosen, in whichever selection mode is active. */
		const fnIsSelected = (key: string): boolean => (multiple ? selected[key] === true : value === key);

		return catalog.Categories.map((name) => ({
			name,
			metrics: catalog.Metrics.filter((metric) => metric.Category === name && fnMatches(metric)),
			/* A search expands everything it matched — hiding a hit behind a heading is worse than a long list. */
			expanded: query.length > 0 || collapsed[name] !== true,
			selectedCount: catalog.Metrics.filter((metric) => metric.Category === name && fnIsSelected(metric.Key)).length
		})).filter((section) => section.metrics.length > 0);
	});

	/** Whether the catalogue loaded but nothing matched the search. */
	let noMatch = $derived(catalog != null && sections.length === 0);

	/** Expands or collapses one category. */
	function fnToggleCategory(name: string): void {
		collapsed = { ...collapsed, [name]: collapsed[name] !== true };
	}

	/** Records a metric being chosen, in whichever selection mode is active. */
	function fnChoose(key: string): void {
		if (multiple) selected = { ...selected, [key]: selected[key] !== true };
		else value = key;
	}
</script>

<div class="relative mb-3">
	<div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
		<i class="fa-solid fa-magnifying-glass text-sm text-gray-400"></i>
	</div>
	<Input bind:value={search} placeholder={$t(placeholder)} />
</div>

<div class="space-y-1 {scrollable ? 'max-h-72 overflow-y-auto pr-1' : ''}">
	{#each sections as section (section.name)}
		<div>
			<button
				type="button"
				class="flex w-full items-center py-1.5 text-left text-sm font-semibold text-gray-700 hover:text-primary-600 dark:text-gray-200"
				onclick={() => fnToggleCategory(section.name)}
			>
				<i
					class="fa-solid {section.expanded ? 'fa-chevron-down' : 'fa-chevron-right'} mr-1.5 w-3 shrink-0 text-xs"
				></i>
				<span class="grow">{$t(section.name)}</span>
				{#if section.selectedCount > 0}
					<Badge color="green" class="mr-1">{section.selectedCount}</Badge>
				{/if}
				<span class="text-xs font-normal text-gray-400">{section.metrics.length}</span>
			</button>

			{#if section.expanded}
				<div class="space-y-1 pb-1 pl-5">
					{#each section.metrics as metric (metric.Key)}
						{#if multiple}
							<Checkbox
								checked={selected[metric.Key] === true}
								onchange={() => fnChoose(metric.Key)}
								title={metric.Description ?? metric.Key}
							>
								<span class="ml-1 inline-flex items-center gap-1">
									{$t(metric.Label)}
									{#if !metric.TimeSeries}<Badge color="purple">{$t('snapshot')}</Badge>{/if}
									{#if metric.Unit === 'percent'}<Badge color="indigo">%</Badge>{/if}
								</span>
							</Checkbox>
						{:else}
							<button
								type="button"
								title={metric.Description ?? metric.Key}
								class="flex w-full items-center gap-1 rounded px-2 py-1 text-left text-sm {value === metric.Key
									? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200'
									: 'hover:bg-primary-500/10'}"
								onclick={() => fnChoose(metric.Key)}
							>
								<span class="inline-flex grow items-center gap-1">
									{$t(metric.Label)}
									{#if !metric.TimeSeries}<Badge color="purple">{$t('snapshot')}</Badge>{/if}
									{#if metric.Unit === 'percent'}<Badge color="indigo">%</Badge>{/if}
								</span>
								{#if value === metric.Key}
									<i class="fa-solid fa-check w-4 shrink-0"></i>
								{/if}
							</button>
						{/if}
					{/each}
				</div>
			{/if}
		</div>
	{/each}

	{#if noMatch}
		<Helper class="py-2">{$t('No metrics match your search.')}</Helper>
	{/if}
</div>
